import PropTypes from 'prop-types'
import { memo, useCallback, useMemo } from 'react'
import Button from 'components/core/Button/Button'
import classNames from 'lib/util/classNames'
import { useSlideshowContext } from '../Slideshow'
import styles from './Navigation.module.scss'

/**
 * The slideshow `Navigation`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Navigation (props) {
  const {
    children,
    className,
    classNameControls,
    classNameNext: classNameNextInput,
    classNamePrevious: classNamePreviousInput
  } = props
  const {
    groupCount,
    hasOverflowRight,
    numSlides,
    onScrollToSlide,
    slideIndex
  } = useSlideshowContext()
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )
  const hasPrevious = useMemo(() => slideIndex > 0, [slideIndex])
  const hasNext = useMemo(() => hasOverflowRight, [hasOverflowRight])
  const classNamePrevious = useMemo(
    () =>
      classNames([
        classNameControls,
        classNamePreviousInput,
        styles.option,
        hasPrevious && styles.enabled
      ]),
    [hasPrevious, classNameControls, classNamePreviousInput]
  )
  const classNameNext = useMemo(
    () =>
      classNames([
        classNameControls,
        classNameNextInput,
        styles.option,
        hasNext && styles.enabled
      ]),
    [hasNext, classNameControls, classNameNextInput]
  )

  /**
   * Handle click previous
   */
  const handleClickPrevious = useCallback(() => {
    onScrollToSlide(Math.max(0, slideIndex - groupCount))
  }, [slideIndex, groupCount])

  /**
   * Handle click next
   */
  const handleClickNext = useCallback(() => {
    onScrollToSlide(Math.min(numSlides - 1, slideIndex + groupCount))
  }, [slideIndex, groupCount, numSlides])

  return (
    <div className={classNameOutput}>
      <div className={styles.content}>{children}</div>
      <div className={styles.left}>
        <div className={classNamePrevious}>
          <Button
            type="symbol"
            icon="arrow_left"
            size="lg"
            color="c"
            onClick={handleClickPrevious}
            useTooltip={true}
            title="Previous"
          />
        </div>
      </div>
      <div className={styles.right}>
        <div className={classNameNext}>
          <Button
            type="symbol"
            icon="arrow_right"
            size="lg"
            color="c"
            onClick={handleClickNext}
            useTooltip={true}
            title="Next"
          />
        </div>
      </div>
    </div>
  )
}

/** @type {object} */
Navigation.propTypes = {
  className: PropTypes.string,
  classNameControls: PropTypes.string,
  classNameNext: PropTypes.string,
  classNamePrevious: PropTypes.string,
  children: PropTypes.node
}

/** @type {object} */
Navigation.defaultProps = {
  className: '',
  classNameControls: '',
  classNameNext: '',
  classNamePrevious: '',
  children: null
}

// Memoize
export default memo(Navigation)
